import type {CompanyConfiguration} from 'src/core/data/api/CompanyConfiguration';

export type CompanyMissingField = 'logoUrl' | 'androidAppUrl' | 'iosAppUrl';

export interface Central {
  id: number;
  name: string;
}

export interface CompanyRow extends Partial<CompanyConfiguration> {
  rowKey: string;
  customerId?: number;
  customerName?: string;
  missing: CompanyMissingField[];
}

const MISSABLE_FIELDS: CompanyMissingField[] = ['logoUrl', 'androidAppUrl', 'iosAppUrl'];

const isBlank = (value?: string) => !value || !value.trim();

/**
 * The list shows both situations in the same table: the configurations that already exist, linked
 * to a customer or not, and the customers with no configuration yet, blank and ready to fill in.
 */
export const buildCompanyRows = (
  configs: CompanyConfiguration[],
  centrals: Central[],
): CompanyRow[] => {
  const nameByCustomer = new Map(centrals.map((central) => [central.id, central.name]));
  const alreadyConfigured = new Set<number>();

  const configRows = configs.map((config) => {
    if (config.customerId) {
      alreadyConfigured.add(config.customerId);
    }

    return {
      ...config,
      rowKey: `cfg-${config.id}`,
      customerName: config.customerId ? nameByCustomer.get(config.customerId) : undefined,
      missing: MISSABLE_FIELDS.filter((field) => isBlank(config[field])),
    };
  });

  const pendingRows = centrals
    .filter((central) => !alreadyConfigured.has(central.id))
    .map((central) => ({
      rowKey: `cust-${central.id}`,
      customerId: central.id,
      customerName: central.name,
      missing: [...MISSABLE_FIELDS],
    }));

  return [...configRows, ...pendingRows];
};
